import React from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import RunningGirl from '@amplify/running-girl';
import { AmplifyGridCell } from '@amplify/amplify-grid';
import { MEASURE } from '../../../../constants/shared/MeasuresConstants';
import handleValidationFunction from './goalValidationService';
import { VALIDATION, STYLE } from '../../../../constants/shared/ZoGConstants';
import { GRADE } from '../../../../constants/shared/GradesConstants';
import { getMiddleFlagPosition } from '../../../probeDetails/shared/runningGirlHelper';

const { OVERALL, MAZE, CLPL, ORF_ACC, FLO_PREC } = MEASURE;
const {
    RUNNING_GIRL_CONTAINER_CSS_STRING,
    PROGRESS_BAR_CONTAINER_CSS_STRING,
    CELL_CONTAINER_CSS_OBJECT,
    COLOR,
} = STYLE;

const { EMPTY, EMPTY_RANGE_MIN, RANGE_MAX, READING_ACCURACY_MAX } = VALIDATION.VALUE;

const ZonesOfGrowthGoalCell = ({
    cutScores,
    goal,
    barColors,
    isOverallNotFinished,
    studentSaveInfo,
    additionalInfo,
    growthRatePrev,
    isDisabled,
    getStrugglingAdvancedGoalForMeasure,
    appName,
}) => {
    const { studentId, measure, grade } = studentSaveInfo;
    const { customGoal, averageRange, zogStatuses, inputRef } = additionalInfo;
    const isPreschool = grade === GRADE.PREKINDERGARTEN;
    const basicComprehensionMeasures = [MAZE, CLPL];
    const readingAccuracyMeasures = [ORF_ACC, FLO_PREC];

    const cellProperties = {
        cellConfig: {
            htmlElement: !isEmpty(cutScores) && !isPreschool && (
                <div className="goal-container">
                    <RunningGirl
                        actualScore={goal}
                        cutScores={cutScores}
                        barColors={barColors}
                        height={40}
                        width={400}
                        customScoreComponent={
                            <div
                                className={`${!isOverallNotFinished ? 'running-girl-flag' : null}`}
                            />
                        }
                        containerCustomStyles={RUNNING_GIRL_CONTAINER_CSS_STRING}
                        barCustomStyles={PROGRESS_BAR_CONTAINER_CSS_STRING}
                        customPosition={getMiddleFlagPosition(cutScores, goal)}
                    />
                </div>
            ),
            cellStyles: {
                ...CELL_CONTAINER_CSS_OBJECT,
                backgroundColor: measure === OVERALL && COLOR.COMPOSITE_BG,
            },
        },
        inputConfig: {
            id: `${studentId}_${measure}_${grade}`,
            value: isOverallNotFinished ? EMPTY : `${customGoal}`,
            min: isEmpty(averageRange) ? EMPTY_RANGE_MIN : averageRange.min,
            max: readingAccuracyMeasures.includes(measure) ? READING_ACCURACY_MAX : RANGE_MAX,
            step: basicComprehensionMeasures.includes(measure) ? 0.5 : 1,
            disabled: isDisabled || measure === OVERALL || isNil(zogStatuses),
            inputStyles: {
                display: (isEmpty(cutScores) || isPreschool) && 'none',
                borderColor: COLOR.INPUT_BORDER,
            },
        },
        validationConfig: {
            validationFunction: (event, setCurrentValue) =>
                handleValidationFunction({
                    event,
                    setCurrentValue,
                    studentSaveInfo,
                    additionalInfo,
                    growthRatePrev,
                    getStrugglingAdvancedGoalForMeasure,
                    appName,
                }),
        },
    };

    return <AmplifyGridCell isEditable cellProperties={cellProperties} reference={inputRef} />;
};

ZonesOfGrowthGoalCell.defaultProps = {
    cutScores: [],
    goal: null,
    barColors: [],
    isOverallNotFinished: false,
    studentSaveInfo: {},
    additionalInfo: {},
    growthRatePrev: {},
    isDisabled: false,
    getStrugglingAdvancedGoalForMeasure: () => {},
    appName: '',
};

ZonesOfGrowthGoalCell.propTypes = {
    cutScores: PropTypes.array,
    goal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    barColors: PropTypes.array,
    isOverallNotFinished: PropTypes.bool,
    studentSaveInfo: PropTypes.object,
    additionalInfo: PropTypes.object,
    growthRatePrev: PropTypes.object,
    isDisabled: PropTypes.bool,
    getStrugglingAdvancedGoalForMeasure: PropTypes.func,
    appName: PropTypes.string,
};

export default ZonesOfGrowthGoalCell;
